"use client"

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react"
import { tokenManager } from "@/lib/token-manager"
import { requestTokenFromParent as requestTokenFromParentMessage } from "@/lib/app-messaging"
import { decodeJwtToken } from "@/lib/jwt-utils"

// Local storage key for custom token
const CUSTOM_TOKEN_KEY = "fbo_custom_token"

const INTERNAL_PROFILE_TYPE_ID = "2"

type AuthContextType = {
  token: string | null
  setToken: (token: string | null) => void
  isAuthenticated: boolean
  isInternalUser: boolean
  userProfileTypeId: string | null
  profileId: string | null
  tokenSource: "url" | "parent" | "custom" | "none"
  saveCustomToken: (token: string) => void
  clearCustomToken: () => void
  requestTokenFromParent: () => void
  handleIframeTokenRequest: (iframeWindow: Window, origin: string) => void
  sendTokenToIframe: (iframeWindow: Window, origin: string) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setTokenState] = useState<string | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
  const [tokenSource, setTokenSource] = useState<"url" | "parent" | "custom" | "none">("none")
  
  // Keep track of iframe windows waiting for tokens (window + expected origin)
  type IframeWaiter = { win: Window; origin: string }
  const [waitingIframes, setWaitingIframes] = useState<Set<IframeWaiter>>(new Set())
  const tokenInitializedRef = useRef(false)

  // Wrapper function to update token and notify token manager
  const setToken = (newToken: string | null) => {
    // Set authentication token
    setTokenState(newToken)
    tokenManager.notifyTokenUpdate(newToken)
    
    // Send token to all waiting iframes
    if (newToken) {
      waitingIframes.forEach(({ win, origin }) => {
        try {
          win.postMessage({ action: "SET_TOKEN", token: newToken }, origin)
        } catch (error) {
          console.error("Failed to send token to iframe:", error)
        }
      })
      // Clear waiting iframes after sending tokens
      setWaitingIframes(new Set())
    }
  }

  // Save a custom token to localStorage
  const saveCustomToken = (customToken: string) => {
    if (typeof window !== "undefined") {
      localStorage.setItem(CUSTOM_TOKEN_KEY, customToken)
      setToken(customToken)
      setTokenSource("custom")
    }
  }

  // Clear the custom token from localStorage
  const clearCustomToken = () => {
    if (typeof window !== "undefined") {
      localStorage.removeItem(CUSTOM_TOKEN_KEY)

      // Set token to null instead of reverting to default
      setToken(null)
      setTokenSource("none")
    }
  }

  // Function to request token from parent window
  const requestTokenFromParent = () => {
    requestTokenFromParentMessage()
  }

  // Handle token request from iframe - act as bridge between parent and iframe
  const handleIframeTokenRequest = useCallback((iframeWindow: Window, origin: string) => {
    if (token) {
      // We have a token, send it immediately
      try {
        iframeWindow.postMessage({ action: "SET_TOKEN", token }, origin)
      } catch (error) {
        console.error("Failed to send token to iframe:", error)
      }
    } else {
      // We don't have a token, add iframe to waiting list and request from parent
      setWaitingIframes(prev => new Set([...prev, { win: iframeWindow, origin }]))
      requestTokenFromParentMessage()
    }
  }, [token])

  // Function to send token to iframe window
  const sendTokenToIframe = useCallback((iframeWindow: Window, origin: string) => {
    if (token && iframeWindow) {
      iframeWindow.postMessage({ action: "SET_TOKEN", token }, origin)
    }
  }, [token])

  // Parse URL parameters on initial load to extract the token
  useEffect(() => {
    if (tokenInitializedRef.current) return
    tokenInitializedRef.current = true

    const extractTokenFromUrl = () => {
      if (typeof window !== "undefined") {
        const urlParams = new URLSearchParams(window.location.search)
        const tokenFromUrl = urlParams.get("token")

        if (tokenFromUrl) {
          // Strip token from URL before any other side effect — prevents exposure
          // in browser history, server logs, Referer headers, and analytics
          urlParams.delete("token")
          const cleanSearch = urlParams.toString()
          const cleanUrl = window.location.pathname
            + (cleanSearch ? "?" + cleanSearch : "")
            + window.location.hash
          history.replaceState(null, "", cleanUrl)

          setToken(tokenFromUrl)
          setTokenSource("url")
          return { token: tokenFromUrl, source: "url" as const }
        }

        // Check for a saved custom token
        const savedToken = localStorage.getItem(CUSTOM_TOKEN_KEY)
        if (savedToken) {
          setToken(savedToken)
          setTokenSource("custom")
          return { token: savedToken, source: "custom" as const }
        }

        // No token from URL or localStorage - try to request from parent
        requestTokenFromParent()

        // Set initial state to null while waiting for parent response
        setToken(null)
        setTokenSource("none")
        return { token: null, source: "none" as const }
      }
      return { token: null, source: "none" as const }
    }

    const { token } = extractTokenFromUrl()
    setIsAuthenticated(!!token)
  }, [])

  // Listen for messages from parent window
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Process messages from parent window
      try {
        const data = event.data

        // Check if this is a SET_TOKEN message
        if (data && data.action === "SET_TOKEN") {
          if (typeof data.token === "string" && data.token.length > 0) {
            // Received token from parent window

            // Set the token from the parent window
            setToken(data.token)
            setTokenSource("parent")

          } else {
            console.warn("AuthContext: Received SET_TOKEN message but token is invalid:", data.token)
          }
        }
      } catch (error) {
        console.error("AuthContext: Error processing message from parent:", error)
      }
    }

    // Add event listener for messages
    if (typeof window !== "undefined") {
      window.addEventListener("message", handleMessage)
              // Message listener registered
    }

    // Clean up event listener
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("message", handleMessage)
        // Message listener removed
      }
    }
  }, [])

  const jwtPayload = useMemo(() => (token ? decodeJwtToken(token) : null), [token])

  const userProfileTypeId = useMemo(() => {
    return jwtPayload?.SecurityContactProfileTypeId ? String(jwtPayload.SecurityContactProfileTypeId) : null
  }, [jwtPayload])

  const profileId = useMemo(() => {
    return jwtPayload?.SecurityContactProfileId ? String(jwtPayload.SecurityContactProfileId) : null
  }, [jwtPayload])

  const isInternalUser = useMemo(() => {
    return userProfileTypeId === INTERNAL_PROFILE_TYPE_ID
  }, [userProfileTypeId])

  // Update authentication status when token changes
  useEffect(() => {
    setIsAuthenticated(!!token)
  }, [token])

  return (
    <AuthContext.Provider
      value={{
        token,
        setToken,
        isAuthenticated,
        isInternalUser,
        userProfileTypeId,
        profileId,
        tokenSource,
        saveCustomToken,
        clearCustomToken,
        requestTokenFromParent,
        handleIframeTokenRequest,
        sendTokenToIframe,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
} 